import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  fetchReparatieIntentTodoList,
  fetchReparatieVoorbeeldUrls,
  isInterneReparatie,
  listAllReparaties,
  listReparateurs,
  sendReparatieQuote,
  type IntentTodoItem,
  type ReparateurDto,
  type ReparatieOpdracht,
  type ReparatieStatus,
  type VolgendeStapActie,
} from '../lib/reparatie';
import { formatCurrency, formatDateTime } from '../lib/format';

type ReparatiesTab = 'overzicht' | 'teplannen';

const STATUS_VOLGORDE: ReparatieStatus[] = [
  'Aanvraag verstuurd',
  'Offerte ontvangen',
  'Offerte naar klant',
  'Klant akkoord',
  'Bij reparateur',
  'Terug in wasserij',
];

const STATUS_COLORS: Record<ReparatieStatus, string> = {
  'Aanvraag verstuurd': 'bg-blue-100 text-blue-700 border-blue-200',
  'Offerte ontvangen': 'bg-amber-100 text-amber-700 border-amber-200',
  'Offerte naar klant': 'bg-purple-100 text-purple-700 border-purple-200',
  'Klant akkoord': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Bij reparateur': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Terug in wasserij': 'bg-teal-100 text-teal-700 border-teal-200',
  'Afgerond': 'bg-slate-100 text-slate-600 border-slate-200',
  'Geannuleerd': 'bg-rose-100 text-rose-700 border-rose-200',
  'Geweigerd': 'bg-rose-100 text-rose-700 border-rose-200',
};

/**
 * Fase-pil per kleed. Fase en tekst komen uit de API (reparatie-fase.ts),
 * dezelfde bron als de orderkaart, zodat beide schermen hetzelfde zeggen.
 */
const FASE_PIL: Record<string, string> = {
  bij_klant: 'border-slate-300 text-slate-600',
  in_wasserij: 'border-slate-300 text-slate-600',
  heen_gepland: 'border-blue-300 text-blue-700',
  bij_reparateur: 'border-indigo-300 text-indigo-700',
  klaar: 'border-emerald-300 text-emerald-700',
  terug_gepland: 'border-blue-300 text-blue-700',
  terug_in_wasserij: 'border-teal-300 text-teal-700',
};

const STATUS_LABEL: Partial<Record<ReparatieStatus, string>> = {
  'Aanvraag verstuurd': 'Wacht op prijs reparateur',
  'Offerte ontvangen': 'Prijs binnen',
  'Offerte naar klant': 'Wacht op klant',
  'Klant akkoord': 'Akkoord',
};

const pil = 'inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border bg-white font-medium';

const KANTOOR_ACTIES: VolgendeStapActie[] = ['inkoop', 'send-quote', 'akkoord', 'plan-heen', 'markeer-gebracht', 'plan-terug', 'wacht-reparateur', 'wacht-klant'];

/** Volgende-stap-filter: wie is aan zet? */
const STAP_FILTERS: Array<{ key: string; label: string; acties: VolgendeStapActie[] }> = [
  { key: 'kantoor', label: 'Kantoor aan zet', acties: KANTOOR_ACTIES },
  { key: 'plannen', label: 'Rit plannen', acties: ['plan-heen', 'plan-terug'] },
  { key: 'reparateur', label: 'Reparateur aan zet', acties: ['wacht-reparatie'] },
  { key: 'chauffeur', label: 'Chauffeur aan zet', acties: ['wacht-chauffeur', 'wacht-terug'] },
  { key: 'klant', label: 'Klant aan zet', acties: ['wacht-klant'] },
  { key: 'wasserij', label: 'Wacht op ophaal/was', acties: ['wacht-ophaal', 'wacht-was'] },
];

export function ReparatiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: ReparatiesTab =
    searchParams.get('tab') === 'teplannen' ? 'teplannen' : 'overzicht';
  const [todoCount, setTodoCount] = useState<number>(0);
  const [opdrachten, setOpdrachten] = useState<ReparatieOpdracht[]>([]);
  const [reparateurs, setReparateurs] = useState<ReparateurDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<ReparatieStatus | ''>('');
  const [filterReparateur, setFilterReparateur] = useState('');
  const [filterStap, setFilterStap] = useState('');
  const [search, setSearch] = useState('');
  const [includeAfgerond, setIncludeAfgerond] = useState(false);

  const switchTab = (next: ReparatiesTab) => {
    if (next === 'overzicht') {
      const sp = new URLSearchParams(searchParams);
      sp.delete('tab');
      setSearchParams(sp, { replace: true });
    } else {
      setSearchParams({ tab: next }, { replace: true });
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, reps] = await Promise.all([
        listAllReparaties({
          status: filterStatus || undefined,
          reparateurId: filterReparateur || undefined,
          includeAfgerond,
        }),
        listReparateurs(),
      ]);
      setOpdrachten(list);
      setReparateurs(reps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterReparateur, includeAfgerond]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filtered = useMemo(() => {
    const stap = STAP_FILTERS.find((f) => f.key === filterStap);
    const q = search.trim().toLowerCase();
    return opdrachten.filter((o) => {
      if (stap && !(o.volgendeStap && stap.acties.includes(o.volgendeStap.actie))) return false;
      if (!q) return true;
      return (
        o.opdrachtNummer.toLowerCase().includes(q) ||
        (o.werkOrderNummer ?? '').toLowerCase().includes(q) ||
        (o.klantNaam ?? '').toLowerCase().includes(q) ||
        (o.reparateurNaam ?? '').toLowerCase().includes(q) ||
        o.items.some((it) => it.wiNaam.toLowerCase().includes(q))
      );
    });
  }, [opdrachten, search, filterStap]);

  const byStatus = useMemo(() => {
    const map = new Map<ReparatieStatus, ReparatieOpdracht[]>();
    for (const o of filtered) {
      const list = map.get(o.status) ?? [];
      list.push(o);
      map.set(o.status, list);
    }
    return map;
  }, [filtered]);

  // Tellers op "wie is aan zet", niet op status: dat is waar kantoor op stuurt.
  const totals = useMemo(() => {
    let kleden = 0;
    let kantoor = 0;
    let plannen = 0;
    let reparateur = 0;
    let chauffeur = 0;
    for (const o of filtered) {
      kleden += o.items.length;
      const a = o.volgendeStap?.actie;
      if (!a) continue;
      if (a === 'plan-heen' || a === 'plan-terug') plannen++;
      if (KANTOOR_ACTIES.includes(a)) kantoor++;
      if (a === 'wacht-reparatie') reparateur++;
      if (a === 'wacht-chauffeur' || a === 'wacht-terug') chauffeur++;
    }
    return { kleden, kantoor, plannen, reparateur, chauffeur };
  }, [filtered]);

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto overflow-y-auto h-full space-y-4">
        <header className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Reparaties</h1>
            {tab === 'overzicht' && (
              <p className="text-sm text-slate-500">
                {filtered.length} opdrachten · {totals.kleden} kleden ·{' '}
                <span className="text-indigo-700 font-medium">{totals.kantoor} kantoor aan zet</span>
                {totals.plannen > 0 && <span className="text-amber-700"> (waarvan {totals.plannen} rit plannen)</span>} ·{' '}
                <span className="text-slate-600">{totals.reparateur} bij reparateur</span> ·{' '}
                <span className="text-slate-600">{totals.chauffeur} bij chauffeur</span>
              </p>
            )}
            {tab === 'teplannen' && (
              <p className="text-sm text-slate-500">
                Bevestigde klantverzoeken zonder repair-order — wacht op planning bij reparateur.
              </p>
            )}
          </div>
        </header>

        <div className="border-b border-slate-200 flex gap-4">
          <button
            type="button"
            onClick={() => switchTab('overzicht')}
            className={clsx(
              'py-2 px-1 text-sm font-medium border-b-2 transition-colors',
              tab === 'overzicht'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )}
          >
            Overzicht
          </button>
          <button
            type="button"
            onClick={() => switchTab('teplannen')}
            className={clsx(
              'py-2 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-2',
              tab === 'teplannen'
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )}
          >
            Te plannen
            {todoCount > 0 && (
              <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                {todoCount}
              </span>
            )}
          </button>
        </div>

        {tab === 'teplannen' && (
          <IntentTodoTab onCountChange={setTodoCount} />
        )}

        <div className={clsx(tab === 'overzicht' ? '' : 'hidden', 'space-y-4')}>
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-slate-600 mb-1">Zoek</label>
          <input
            type="text"
            placeholder="Opdracht, WO, klant, reparateur, WI…"
            className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
          <select
            className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as ReparatieStatus | '')}
          >
            <option value="">Alle (open)</option>
            {STATUS_VOLGORDE.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
            <option value="Afgerond">Afgerond</option>
            <option value="Geannuleerd">Geannuleerd</option>
            <option value="Geweigerd">Geweigerd</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Wie is aan zet</label>
          <select
            className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
            value={filterStap}
            onChange={(e) => setFilterStap(e.target.value)}
          >
            <option value="">Iedereen</option>
            {STAP_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Reparateur</label>
          <select
            className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
            value={filterReparateur}
            onChange={(e) => setFilterReparateur(e.target.value)}
          >
            <option value="">Alle</option>
            {reparateurs.map((r) => (
              <option key={r.id} value={r.id}>{r.naam}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={includeAfgerond}
            onChange={(e) => setIncludeAfgerond(e.target.checked)}
          />
          Toon afgerond/geannuleerd
        </label>
      </div>

      {loading && <p className="text-sm text-slate-500">Laden…</p>}
      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
          {error}
        </p>
      )}

      {!loading && filtered.length === 0 && !error && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-slate-500">Geen reparaties gevonden met deze filters.</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-6">
          {STATUS_VOLGORDE.concat(includeAfgerond ? ['Afgerond' as ReparatieStatus, 'Geannuleerd' as ReparatieStatus, 'Geweigerd' as ReparatieStatus] : []).map((status) => {
            const inStatus = byStatus.get(status) ?? [];
            if (inStatus.length === 0) return null;
            return (
              <section key={status}>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className={clsx('text-xs font-medium px-2 py-0.5 rounded border', STATUS_COLORS[status])}>
                    {STATUS_LABEL[status] ?? status}
                  </span>
                  <span className="text-xs text-slate-500">{inStatus.length} opdrachten</span>
                </div>
                <div className="space-y-2">
                  {inStatus.map((o) => (
                    <OpdrachtRow key={o.id} opdracht={o} onChanged={fetchAll} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
        </div>
      </div>
    </>
  );
}

function IntentTodoTab({ onCountChange }: { onCountChange: (n: number) => void }) {
  const [items, setItems] = useState<IntentTodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchReparatieIntentTodoList();
      setItems(list);
      onCountChange(list.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) return <p className="text-sm text-slate-500">Laden…</p>;
  if (error) {
    return (
      <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
        {error}
      </p>
    );
  }
  if (items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <p className="text-slate-500">🎉 Geen kleden wachten op een repair-order.</p>
        <p className="text-xs text-slate-400 mt-1">
          Bevestigde klantverzoeken zonder reparatie-opdracht verschijnen hier.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((it) => (
        <IntentTodoRow key={it.wiId} item={it} />
      ))}
    </div>
  );
}

function IntentTodoRow({ item }: { item: IntentTodoItem }) {
  const navigate = useNavigate();
  const handleNaarOrder = () => {
    if (!item.werkOrderId) return;
    navigate(`/orders/${item.werkOrderId}?openReparatie=1&wiId=${item.wiId}`);
  };
  return (
    <div className="bg-white rounded-lg border border-amber-200 p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-mono font-medium text-slate-900">{item.wiName}</span>
            {item.werkOrderName && item.werkOrderId && (
              <Link
                to={`/orders/${item.werkOrderId}`}
                className="text-xs font-mono text-slate-500 hover:underline"
              >
                {item.werkOrderName}
              </Link>
            )}
            {item.klantNaam && (
              <span className="text-xs text-slate-700">· {item.klantNaam}</span>
            )}
            <span className="bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5 rounded font-medium">
              ✓ Bevestigd
            </span>
          </div>
          {item.klantverzoek && (
            <p className="text-sm text-slate-700 italic">"{item.klantverzoek}"</p>
          )}
          <p className="text-[11px] text-slate-400 mt-1">
            {item.bron && `via ${item.bron}`}
            {item.datumBevestigd && ` · ${formatDateTime(item.datumBevestigd)}`}
          </p>
        </div>
        <button
          type="button"
          onClick={handleNaarOrder}
          disabled={!item.werkOrderId}
          className="bg-amber-600 text-white text-sm font-semibold px-3 py-1.5 rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
          Maak repair-order →
        </button>
      </div>
    </div>
  );
}

function OpdrachtRow({ opdracht: o, onChanged }: { opdracht: ReparatieOpdracht; onChanged?: () => void }) {
  const linkTarget = o.werkOrderId ? `/orders/${o.werkOrderId}` : '#';
  const isOfferteOntvangen = o.status === 'Offerte ontvangen';
  const isIntern = isInterneReparatie(o);
  const voorbeeldPaths = o.voorbeeldFotoPaths ?? [];
  const [voorbeeldUrls, setVoorbeeldUrls] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOfferteOntvangen || voorbeeldPaths.length === 0) return;
    let cancelled = false;
    fetchReparatieVoorbeeldUrls(voorbeeldPaths)
      .then((urls) => {
        if (!cancelled) setVoorbeeldUrls(urls.filter((u): u is string => !!u));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.id, isOfferteOntvangen]);

  const handleAkkoord = async () => {
    setSending(true);
    setActionError(null);
    try {
      const res = await sendReparatieQuote(o.id);
      if (!res.success) {
        setActionError(res.message || 'Versturen naar klant mislukt');
      } else {
        onChanged?.();
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Versturen naar klant mislukt');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={linkTarget}
            className="text-sm font-medium text-indigo-700 hover:underline"
          >
            {o.opdrachtNummer}
          </Link>
          {o.werkOrderNummer && (
            <Link to={linkTarget} className="text-xs font-mono text-slate-500 hover:underline">
              {o.werkOrderNummer}
            </Link>
          )}
          {o.klantNaam && <span className="text-xs text-slate-700">· {o.klantNaam}</span>}
          {o.reparateurNaam && (
            <span className="text-xs text-slate-500">→ {o.reparateurNaam}</span>
          )}
          {isIntern && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-violet-100 text-violet-700"
              title="Eigen schade of coulance. Geen offerte, geen factuurregel, klant betaalt niets."
            >
              Intern — wij betalen
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {o.verkoopBedrag != null && (
            <span className="text-sm font-medium text-slate-900">{formatCurrency(o.verkoopBedrag)}</span>
          )}
          <span className="text-xs text-slate-400">
            {o.aantalKleden} {o.aantalKleden === 1 ? 'kleed' : 'kleden'}
          </span>
        </div>
      </div>

      {o.items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mt-2">
          {o.items.map((it) => {
            return (
              <div
                key={it.wiId}
                className="flex items-center gap-2 bg-slate-50/60 rounded border border-slate-100 px-2 py-1.5"
              >
                <span className="text-xs font-mono font-medium text-slate-900 flex-shrink-0">
                  {it.wiNaam}
                </span>
                <span className={clsx(pil, 'flex-shrink-0', FASE_PIL[it.fase ?? 'bij_klant'] ?? 'border-slate-300 text-slate-600')}>
                  {it.faseLabel ?? it.fase ?? '—'}
                </span>
                {it.opdrachtTekst && (
                  <span className="text-[11px] text-slate-500 truncate">{it.opdrachtTekst}</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {o.volgendeStap && (
        <p className="mt-2 text-xs text-slate-700">
          <span className="font-medium text-slate-900">Volgende stap: </span>
          {o.volgendeStap.tekst}
        </p>
      )}

      {isOfferteOntvangen && isIntern && (
        <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50/60 p-3">
          <div className="flex items-center gap-3 text-sm flex-wrap">
            {o.inkoopBedrag != null && (
              <span className="text-slate-700">
                Onze kosten: <strong>{formatCurrency(o.inkoopBedrag)}</strong>
              </span>
            )}
            {o.doorlooptijdWerkdagen != null && (
              <span className="text-slate-600">
                ± {o.doorlooptijdWerkdagen} werkdag{o.doorlooptijdWerkdagen === 1 ? '' : 'en'}
              </span>
            )}
          </div>
          <p className="text-xs text-violet-800 mt-1">
            Interne reparatie: geen offerte naar de klant. Plan de heen-rit op de orderpagina.
          </p>
        </div>
      )}

      {isOfferteOntvangen && !isIntern && (
        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-3 text-sm flex-wrap">
                {o.inkoopBedrag != null && (
                  <span className="text-slate-700">Inkoop: <strong>{formatCurrency(o.inkoopBedrag)}</strong></span>
                )}
                {o.verkoopBedrag != null && (
                  <span className="text-slate-700">Klantprijs: <strong>{formatCurrency(o.verkoopBedrag)}</strong></span>
                )}
                {o.doorlooptijdWerkdagen != null && (
                  <span className="text-slate-600">
                    ± {o.doorlooptijdWerkdagen} werkdag{o.doorlooptijdWerkdagen === 1 ? '' : 'en'}
                  </span>
                )}
              </div>
              {o.voorbeeldToelichting && (
                <p className="text-xs text-slate-600">{o.voorbeeldToelichting}</p>
              )}
              {voorbeeldUrls.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {voorbeeldUrls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer">
                      <img
                        src={url}
                        alt={`Voorbeeld ${i + 1}`}
                        className="w-12 h-12 object-cover rounded border border-amber-200"
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={handleAkkoord}
                disabled={sending}
                className="bg-indigo-600 text-white text-sm font-semibold px-3 py-1.5 rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {sending ? 'Versturen…' : 'Akkoord & stuur naar klant'}
              </button>
              {actionError && (
                <span className="text-xs text-red-600 max-w-[220px] text-right">{actionError}</span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="text-[11px] text-slate-400 flex flex-wrap gap-x-3 mt-2">
        {o.klantAkkoordOp && <span>Akkoord {formatDateTime(o.klantAkkoordOp)}</span>}
        {o.gebrachtOp && <span>Bij reparateur sinds {formatDateTime(o.gebrachtOp)}</span>}
        {o.opgehaaldOp && <span>Terug sinds {formatDateTime(o.opgehaaldOp)}</span>}
      </div>
    </div>
  );
}
